import * as numeral from 'numeral';
import { InputModel } from '../components/SectionWrapper/SectionWrapper';
import { Addon } from '../components/Inputs/TextInput/TextInput';
import { calcConfig, FMCs, growths } from '../content/content';
import {
  formatDate,
  formatFrequency,
  getCurrentAge,
  getPlanMinusLumpSumValue,
  splitYearsAndMonths,
  getAnnualValue,
  getPercentage,
  getExpectedDeathAge,
  printDate,
  printDateComma,
  getDrawdown,
} from './calculatorHelpers';

const RunProjection = require('../calculator/ctcModeller');
require('numeral/locales/en-gb');

numeral.locale('en-gb');
numeral.defaultFormat('$0,0.00');

interface ModellerReturn {
  AdviserIncome: number[];
  Contributions: number[];
  Fund: number[];
  FundAtPRA: number;
  FundRanOutAge: number;
  GrossTargetAmount: number;
  GuaranteedIncome: number;
  Income: number[];
  OtherIncome: number;
  ProbabilityOfSurvival: number[];
}

interface CalculateResults {
  currentResults: ParsedModellerReturn;
  plannedResults: ParsedModellerReturn;
  longLifeResults: ParsedModellerReturn;
}

interface ParsedModellerReturn {
  ranOutAge: number;
  probabilityOfSurvival: number[];
}

export const formatModel = (model: InputModel, drawdown: string, subtractLumpSum: boolean) => Object.assign({
  ProjectionMethod: 3,
  DateOfBirth: formatDate(model.birthDate),
  Gender: model.gender.split('')[0],
  Location: model.residence,
  PRA: Math.trunc(getCurrentAge(model.birthDate) / 12),
  FundValue: subtractLumpSum ? getPlanMinusLumpSumValue(Number(model.planValue), Number(model.lumpsum || 0), model.lumpsumType) : Number(model.planValue),
  Income: [{
    IncomeStartAge: Math.trunc(getCurrentAge(model.birthDate) / 12),
    Amount: Number(drawdown),
    Frequency: formatFrequency(model.incomeFrequency),
    Escalation: calcConfig.IncomeEscalation,
    Term: calcConfig.IncomeTerm,
  }],
  PCLSAge: Math.trunc(getCurrentAge(model.birthDate) / 12),
  FMC: FMCs[model.isCash],
  Growth: growths[model.isCash],
}, calcConfig.inputModel);

export const parseResult = (results: ModellerReturn, drawdown: number, age: number) => {
  const { Income, ProbabilityOfSurvival: probabilityOfSurvival } = results;

  if (drawdown === 0 && Income[0] === 0) {
    return {
      ranOutAge: 1440,
      probabilityOfSurvival,
    };
  }

  const yearsLeft = Income.filter((income: number) => income === drawdown).length;
  const monthsLeft = Math.round((Income[Income.length - 1] / drawdown) * 12);

  return {
    ranOutAge: age + (yearsLeft * 12) + monthsLeft,
    probabilityOfSurvival,
  };
};

export const runModeller = (model: InputModel, drawdown: string, subtractLumpSum: boolean) => parseResult(
  RunProjection(formatModel(model, drawdown, subtractLumpSum), false, false),
  getDrawdown(drawdown, model.incomeFrequency),
  getCurrentAge(model.birthDate)
);

export const getResults = (results: CalculateResults, model: InputModel) => {
  const difference = results.currentResults.ranOutAge - results.plannedResults.ranOutAge;
  const differenceParsed = splitYearsAndMonths(Math.abs(difference));
  const isLumpsumPercentage = model.lumpsumType === Addon.Percentage;
  const currentAge = getCurrentAge(model.birthDate);
  const lifeExpectancy = getExpectedDeathAge(results.longLifeResults.probabilityOfSurvival, Math.trunc(currentAge / 12));
  const currentAmount = getAnnualValue(model.currentIncome, model.incomeFrequency);
  const plannedAmount = getAnnualValue(model.plannedIncome, model.incomeFrequency);
  const currentAgeRunsOut = splitYearsAndMonths(results.currentResults.ranOutAge);
  const plannedAgeRunsOut = splitYearsAndMonths(results.plannedResults.ranOutAge);
  const currentWillLast = splitYearsAndMonths(results.currentResults.ranOutAge - currentAge);
  const plannedWillLast = splitYearsAndMonths(results.plannedResults.ranOutAge - currentAge);
  const couldRunOut = (lifeExpectancy * 12) - results.plannedResults.ranOutAge;

  return {
    detail: {
      timeFrame: `${printDate(differenceParsed)} ${difference > 0 ? 'sooner' : 'later'}`,
      incomeFrequency: `regular ${model.incomeFrequency.toLowerCase()} income`,
      currentIncome: numeral(model.currentIncome).format(),
      plannedIncome: numeral(model.plannedIncome).format(),
      lumpsum: isLumpsumPercentage ? `${model.lumpsum}%` : numeral(model.lumpsum || 0).format(),
    },
    tables: {
      current: {
        amount: numeral(currentAmount).format(),
        percentage: getPercentage(currentAmount, model.planValue),
        willLast: printDate(currentWillLast),
        whenYouAre: `when you are ${printDateComma(currentAgeRunsOut)}`,
      },
      planned: {
        amount: numeral(plannedAmount).format(),
        percentage: getPercentage(plannedAmount, model.planValue),
        willLast: printDate(plannedWillLast),
        whenYouAre: `when you are ${printDateComma(plannedAgeRunsOut)}`,
      },
      yearsFromDeath: `${printDate(splitYearsAndMonths(couldRunOut))} ${couldRunOut > 0 ? 'earlier' : 'later'}`,
      lifeExpectancy: `${lifeExpectancy} years old.`,
    },
  };
};

export const calculate = (model: InputModel) => getResults({
  currentResults: runModeller(model, model.currentIncome, false),
  plannedResults: runModeller(model, model.plannedIncome, true),
  longLifeResults: runModeller(model, '0', false),
}, model);



// WEBPACK FOOTER //
// ./src/utilities/calculator.ts